import { useMemo } from "react";
import { CheckCircle2, ArrowUpCircle, HeartHandshake, Timer } from "lucide-react";

import MemberFunnel from "../MemberFunnel";
import MemberMiniList from "../MemberMiniList";
import StatRow from "../StatRow";
import TrendChart from "../TrendChart";
import { PageStack, TwoCol } from "../ui";
import { PageProps } from "./types";
import { conversionTrend, funnelInsights, trialsSeries } from "../../../utils/partnerMetrics";

const pct = (n: number | null) => (n === null ? "—" : `${n}%`);

const FunnelPage = ({ members, loading }: PageProps) => {
  const insights = useMemo(() => funnelInsights(members), [members]);
  const trials = useMemo(() => trialsSeries(members), [members]);
  const conversion = useMemo(() => conversionTrend(members), [members]);

  return (
    <PageStack>
      <MemberFunnel members={members} loading={loading} />
      <StatRow
        loading={loading}
        stats={[
          {
            label: "Check-in Rate",
            value: pct(insights.checkInRate),
            icon: CheckCircle2,
            hint: "Trial buyers who came in",
          },
          {
            label: "Upgrade Rate",
            value: pct(insights.upgradeRateAfterCheckIn),
            icon: ArrowUpCircle,
            hint: "Checked-in trials who upgraded",
          },
          {
            label: "Still Active",
            value: pct(insights.retentionRate),
            icon: HeartHandshake,
            hint: "Upgraded members still with you",
          },
          {
            label: "Time to Upgrade",
            value:
              insights.medianDaysToUpgrade === null ? "—" : `${insights.medianDaysToUpgrade} days`,
            icon: Timer,
            hint: "Median from trial start",
          },
        ]}
      />
      <TwoCol>
        <TrendChart
          title="New Trials"
          data={trials}
          loading={loading}
          color="#b0772b"
          caption="this month"
        />
        <TrendChart
          title="Trial → Paid Conversion"
          data={conversion}
          loading={loading}
          color="#8a4b9c"
          valueSuffix="%"
          caption="of trial members upgraded"
        />
      </TwoCol>
      <MemberMiniList
        title="Trials Who Haven't Checked In"
        members={insights.activeTrialsNotCheckedIn}
        loading={loading}
        emptyMessage="Every active trial member has checked in."
        limit={20}
      />
    </PageStack>
  );
};

export default FunnelPage;
